import { useState } from "react";
import { BackArrow, SaveIcon } from "allIcons";
import {
  AddComponent,
  BodyContainer,
  TextEditor,
  HeaderContainer,
  IconWrapper,
  InputField,
  PreviewCard,
  PrimaryHeading,
  RightMainContainer,
  SecondaryHeading,
  MainButton,
  DropDownCard,
  Loader,
  PopOver,
  DeleteButton,
  SelectField,
  ToggleCard,
} from "commonUI";
import { useParams, useNavigate } from "react-router-dom";
import { updateTask, useTasks } from "api/templateLibrary/tasks";
import { useTags } from "api/siteSettings/Tags";
import { useMutation } from "@tanstack/react-query";
import { addTask, deleteTask } from "api/templateLibrary/tasks";
import { useDispatch, useSelector } from "react-redux";
import {
  addSingleTask,
  deleteSingleTask,
  updateSingleTask,
} from "redux/reducers/templateLibrary/tasks";
import { useNotification } from "notification";
import { useEffect } from "react";
import { useUserPermissions, useUserRole } from "hooks/user";
import { getUserSavePermission, isEqual } from "helpers";
import {
  invalidateTemplatePhases,
  usePhases,
} from "api/templateLibrary/phases";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function AddEditTask({previousPath = `/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks`, isSiteTemplate}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notification = useNotification();
  const { userData } = useSelector((state) => state.user);
  const { data: tags } = useTags();
  const { data: tasks, isLoading } = useTasks();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();
  const { data: phases, isLoading: loadingPhases } = usePhases();

  const { isSuperAdmin } = useUserRole();

  const [inputValues, setInputValues] = useState({
    title: "",
    message: "",
    tags: [],
    phase_id: "",
    phase_due_date_auto: 0,
  });
  const [openTags, setOpenTags] = useState(false);
  useEffect(() => {
    if (isLoading || !tasks) return;
    if (id === "add") return;
    if (tasks) {
      if (id === "add") return;
      let task = tasks.filter((task) => Number(task.id) === Number(id))[0];
      if (!task) {
        return;
      }
      setInputValues({
        ...task,
        title: task.title,
        description: task.message ?? task.description,
        tags:
          typeof task.tags != "object"
            ? Array.isArray(JSON.parse(task.tags))
              ? JSON.parse(task.tags ?? "[]")
              : [JSON.parse(task.tags ?? "[]")]
            : task.tags ?? [],
      });
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [isLoading, id, tasks]);

  const addMutation = useMutation(addTask, {
    onSuccess: (data) => {
      navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/${data.data.id}`);
      dispatch(addSingleTask(data.data));
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.added"),
      });
      dispatch(invalidateTemplatePhases());
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const updateMutation = useMutation(updateTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.updated"),
      });
      dispatch(updateSingleTask(data.data));
      dispatch(invalidateTemplatePhases());
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const deleteMutation = useMutation(deleteTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.deleted"),
      });
      dispatch(deleteSingleTask(data.id));
      navigate(previousPath);
      dispatch(invalidateTemplatePhases());
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function handleSave() {
    if (inputValues.title === "" || inputValues.message === "") {
      notification({
        type: "error",
        message:
          inputValues.title === ""
            ? t("common.input.error.titleRequired")
            : t("common.input.description.descriptionRequired"),
      });
      return;
    }
    if (id === "add") {
      addMutation.mutate({
        ...inputValues,
        name: userData.name,
        system_status: isSuperAdmin ? "1" : "0",
        requested_from: [
          {
            user_avatar: userData.user_avatar,
            role: userData.rolesData?.[0]?.role_title ?? "Client",
            name: userData.name,
            group: "Admin",
            id: userData.id,
          },
        ],
      });
    } else {
      updateMutation.mutate({ ...inputValues, id });
    }
  }
  return (
    <>
      <RightMainContainer>
        {addMutation.isLoading ||
        updateMutation.isLoading ||
        deleteMutation.isLoading ? (
          <Loader loader="block" />
        ) : (
          ""
        )}
        <HeaderContainer>
          <IconWrapper>
            <BackArrow onClick={() => navigate(previousPath)} />
            <PrimaryHeading className="clamp-text">
              {id === "add"
                ? t("libraryTemplate.tasks.addTask")
                : inputValues.title || ""}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={
                !isSiteTemplate &&
                !isEqual(id, "add") &&
                (!isEqual(inputValues.system_status, 1) || isSuperAdmin) &&
                delete_templates
              }
              onDelete={() => deleteMutation.mutate(id)}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              deleteMessage={t("libraryTemplate.delete.message")}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={handleSave}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("common.input.title.label")}
            placeholder={t("common.input.title.placeholder")}
            value={inputValues.title}
            setValue={(value) =>
              setInputValues({ ...inputValues, title: value })
            }
          />
          <TextEditor
            label={t("common.input.description.label")}
            placeholder={t("common.input.description.placeholder")}
            editorClass="mt-2"
            value={inputValues.message}
            reRender={id === "add" ? false : inputValues.message ? true : false}
            setValue={(value) =>
              setInputValues({ ...inputValues, message: value })
            }
            blur={openTags}
          />
          <SelectField
            placeholder={t("common.input.associatedPhase.placeholder")}
            className="mt-3 gap-10 mb-3"
            label={t("common.input.associatedPhase.label")}
            value={inputValues.phase_id}
            setValue={(value) =>
              setInputValues({ ...inputValues, phase_id: value })
            }
          >
            {phases.map((phase) => (
              <option key={phase.id} value={phase.id}>
                {phase.title}
              </option>
            ))}
          </SelectField>
          <ToggleCard
            labelClass="mb-2"
            label={t("common.input.dueDate.automaticallyBasedOnPhase")}
            value={isEqual(inputValues.phase_due_date_auto, 1)}
            setValue={(value) =>
              setInputValues({
                ...inputValues,
                phase_due_date_auto: value ? 1 : 0,
              })
            }
            isLoading={loadingPhases}
            disabled={!inputValues.phase_id}
          />
          <div className="mt-3">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            <div className="flex items-center gap-5 flex-wrap">
              {(inputValues?.tags ?? []).map((tag, index) => (
                <PreviewCard
                  color={
                    isEqual(tag.color?.[0] ?? "", "#")
                      ? tag.color
                      : "#" + tag.color ?? ""
                  }
                  title={tag.text || tag.tag_title}
                  icon={true}
                  className="m-0"
                  key={index}
                  onCross={() => {
                    setInputValues((pre) => ({
                      ...pre,
                      tags: pre.tags.filter((item) => item.id !== tag.id),
                    }));
                  }}
                />
              ))}
            </div>
          </div>
          <AddComponent
            onClick={() => setOpenTags(true)}
            title={t("common.input.tags.addTag")}
          >
            <PopOver
              open={openTags}
              onClose={() => setOpenTags(false)}
              className="absolute bottom-full mb-2"
              style={{
                width: "270px",
                top: "auto",
                right: "auto",
                bottom: "calc(100% + 10px)",
              }}
            >
              <DropDownCard
                title={t("common.input.tags.label")}
                listStyle={true}
                className="relative-important"
                filterSelectedItems
                selectedValues={inputValues.tags.map((item) => item.id)}
                list={tags?.map((ele) => ({
                  ...ele,
                  index: ele.id,
                  text: ele.tag_title,
                }))}
                onSelect={(item) => {
                  if (
                    inputValues.tags.find(
                      (i) => Number(i.id) === Number(item.id)
                    )
                  ) {
                    return;
                  }
                  setInputValues((pre) => ({
                    ...pre,
                    tags: [
                      ...pre.tags,
                      {
                        id: item.id,
                        text: item.tag_title,
                        color: item.color,
                      },
                    ],
                  }));
                }}
              />
            </PopOver>
          </AddComponent>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
